import React, { useEffect, useRef, useState } from 'react';
import './RichTextEditor.css';

const RichTextEditor = ({ value = '', onChange, placeholder = '' }) => {
  const editorRef = useRef(null);
  const [toolbarVisible, setToolbarVisible] = useState(false);

  // Helper to convert plain text \n to <br> for contentEditable
  const processTextForEditor = (text) => {
    if (!text) return '';
    // Check if text already contains HTML tags
    const hasHTML = /<[a-z][\s\S]*>/i.test(text);
    if (hasHTML) {
      // Already HTML, ensure \n are converted to <br>
      return text.replace(/\n/g, '<br>');
    }
    // Plain text, convert \n to <br> and escape HTML entities
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
  };

  useEffect(() => {
    if (editorRef.current) {
      const processedValue = processTextForEditor(value);
      const currentContent = editorRef.current.innerHTML;
      if (processedValue !== currentContent) {
        editorRef.current.innerHTML = processedValue;
      }
    }
  }, [value]);

  const handleInput = (e) => {
    if (onChange) {
      onChange(e.target.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    // Convert \n to <br> when pasting
    const htmlText = text.replace(/\n/g, '<br>');
    document.execCommand('insertHTML', false, htmlText);
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // Handle Enter key to insert <br> instead of <div>
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertHTML', false, '<br>');
      if (onChange && editorRef.current) {
        onChange(editorRef.current.innerHTML);
      }
    }
  };

  return (
    <div className="rich-text-editor">
      <div className="toolbar">
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => execCommand('bold')}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => execCommand('italic')}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => execCommand('underline')}
          title="Underline"
        >
          <u>U</u>
        </button>
        <div className="toolbar-separator" />
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => execCommand('justifyLeft')}
          title="Align Left"
        >
          ⬅
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => execCommand('justifyCenter')}
          title="Center"
        >
          ⬌
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => execCommand('justifyRight')}
          title="Align Right"
        >
          ➡
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => execCommand('justifyFull')}
          title="Justify"
        >
          ⬌
        </button>
        <div className="toolbar-separator" />
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => execCommand('insertUnorderedList')}
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => execCommand('insertOrderedList')}
          title="Numbered List"
        >
          1. List
        </button>
        <div className="toolbar-separator" />
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => {
            const url = prompt('Enter URL:');
            if (url) execCommand('createLink', url);
          }}
          title="Insert Link"
        >
          🔗
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => execCommand('removeFormat')}
          title="Remove Format"
        >
          🧹
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        className="editor-content"
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
      />
    </div>
  );
};

export default RichTextEditor;

